import { VIN_CHARACTERS_REGEX } from 'common/constants'
import { StyledCameraScanner } from 'components/Share/CameraScanner.styled'
import { WebScanner } from '@vauto/webscanner-react'
import { useEffect, useRef, useState } from 'react'

interface IScanVINProps {
  onComplete: (vin: string | null) => void
}

export const ScanVIN = ({ onComplete }: IScanVINProps) => {
  const cameraContainerRef = useRef<HTMLDivElement>(null)
  const [isFullscreenFallback, setIsFullscreenFallback] = useState(false)

  const handleCancel = () => {
    onComplete(null)
  }

  const applyFullscreenStyles = (element: HTMLElement) => {
    element.style.position = 'fixed'
    element.style.top = '0'
    element.style.left = '0'
    element.style.width = '100vw'
    element.style.height = '100vh'
    element.style.zIndex = '2147483647'
    element.style.background = '#000'
  }

  const removeFullscreenStyles = (element: HTMLElement) => {
    element.style.position = ''
    element.style.top = ''
    element.style.left = ''
    element.style.width = ''
    element.style.height = ''
    element.style.zIndex = ''
    element.style.background = ''
  }

  useEffect(() => {
    if (!cameraContainerRef.current) return

    const container = cameraContainerRef.current
    let fullscreenFallbackActive = false

    const attemptFullscreen = async () => {
      try {
        await launchIntoFullscreen(container)
      } catch (err) {
        console.warn('Fullscreen API not available, using fallback mode:', err)
        // Use fallback fullscreen mode
        applyFullscreenStyles(container)
        container.classList.add('fullscreen-fallback')
        setIsFullscreenFallback(true)
        fullscreenFallbackActive = true
      }
    }

    // Always apply initial fullscreen styles for immediate effect
    applyFullscreenStyles(container)

    attemptFullscreen()

    const handleFullScreenChange = () => {
      if (!document.fullscreenElement && !fullscreenFallbackActive) {
        handleCancel()
      }
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        handleCancel()
      }
    }

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCancel()
      }
    }

    // Add event listeners
    document.addEventListener('fullscreenchange', handleFullScreenChange)
    document.addEventListener('webkitfullscreenchange', handleFullScreenChange)
    document.addEventListener('mozfullscreenchange', handleFullScreenChange)
    document.addEventListener('MSFullscreenChange', handleFullScreenChange)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    document.addEventListener('keydown', handleEscapeKey)

    return () => {
      // Cleanup event listeners
      document.removeEventListener('fullscreenchange', handleFullScreenChange)
      document.removeEventListener('webkitfullscreenchange', handleFullScreenChange)
      document.removeEventListener('mozfullscreenchange', handleFullScreenChange)
      document.removeEventListener('MSFullscreenChange', handleFullScreenChange)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      document.removeEventListener('keydown', handleEscapeKey)

      // Exit fullscreen if active
      if (document.fullscreenElement) {
        document.exitFullscreen().catch((err) => console.error('Error exiting fullscreen:', err))
      }

      // Remove fallback styling
      if (container) {
        removeFullscreenStyles(container)
        container.classList.remove('fullscreen-fallback')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleScan = (scanData: any) => {
    if (scanData?.vin) {
      const vin = scanData.vin.toUpperCase()
      const check = vin.split('').every((key: string) => VIN_CHARACTERS_REGEX.test(key))

      if (check && vin.length === 17) {
        onComplete(vin)
      }
    }
  }

  const launchIntoFullscreen = async (element: HTMLElement): Promise<void> => {
    // Try all available fullscreen methods
    const fullscreenMethods = [
      () => element.requestFullscreen?.({ navigationUI: 'hide' }),
      () => (element as any).webkitRequestFullscreen?.(),
      () => (element as any).mozRequestFullScreen?.(),
      () => (element as any).msRequestFullscreen?.()
    ]

    for (const method of fullscreenMethods) {
      try {
        const result = method()
        if (result instanceof Promise) {
          await result
          return
        } else if (result !== undefined) {
          return
        }
      } catch (err) {
        // Continue to next method
        continue
      }
    }

    // If no fullscreen method works, throw error to trigger fallback
    throw new Error('No fullscreen API available')
  }

  return (
    <div
      id="camera-container"
      ref={cameraContainerRef}
      className={isFullscreenFallback ? 'fullscreen-fallback' : ''}
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        zIndex: 1
      }}
    >
      <StyledCameraScanner>
        <WebScanner id="scan-vin" onScan={handleScan} onCancel={handleCancel} />
      </StyledCameraScanner>
    </div>
  )
}

export default ScanVIN
